import React, { useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import {
    Grid,
    Typography,
    Button,
    Stepper,
    Step,
    StepLabel,
    TextField,
    useTheme,
    CardHeader,
    Divider,
    Box,
    styled,
    StepConnector,
    darken,
    Tabs,
    Tab,
    IconButton,
    Tooltip
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import FormModal from '../../common/formModal';
import SelectComponent from '../../components/Select';
import JsonEditor from '../../components/CustomJsonEditor';
import Styles from '../../theme/commonCss/Styles';
import { palette } from '../../theme/palette';
import Ajv from 'ajv';
import AjvErrors from 'ajv-errors';
import addFormats from 'ajv-formats';
import { connect } from 'react-redux';
import { useDispatch } from 'react-redux';
import SearchField from '../../common/searchField';
import { ReactComponent as CloseIcon } from '../../assets/icons/main/close-icon.svg';
import { ReactComponent as FailedIcon } from '../../assets/icons/main/close-circle-icon.svg';
import { ReactComponent as WarningIcon } from '../../assets/icons/main/alertIcon.svg';
import { ReactComponent as ReloadIcon } from '../../assets/icons/main/restore-icon.svg';
import { maxLength, minLength } from '../../utils/form-validations';
import AssetsTreeView from './assetsList';
import { schemas, stepperConfig } from './form';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const CustomStyles: any = {
    errorTypo: {
        fontWeight: 400,
        fontSize: '1rem',
        textAlign: 'center'
    },
    errorTitle: {
        fontFamily: 'Open Sans',
        fontWeight: 600,
        fontSize: '20px',
        lineHeight: '100%',
        letterSpacing: '0.5px',
        textAlign: 'center',
        textTransform: 'uppercase',
        paddingTop: '24px'
    }
}

const ajv = new Ajv({ allErrors: true });
addFormats(ajv); // Add support for additional formats like "uri"
AjvErrors(ajv);

// Compile schemas
const validateCustomApplication = ajv.compile(schemas.customApplication);
const validateGrafanaDashboard = ajv.compile(schemas.grafanaDashboard);
const validateJsonTemplateForm = ajv.compile(schemas.jsonTemplateForm);

const CenteredMessageBox = ({ message, showLoading, icon, messageType, handleReload }: any) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minHeight: '200px',
                height: 'inherit'
            }}
        >
            {showLoading ? (<Box> <CircularProgress /> </Box>) :
                <Fragment>
                    <Box sx={{ height: '100px', width: '100px', borderRadius: '50%', backgroundColor: messageType === 'warning' ? '#FF88001A' : '#F614141A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {messageType === 'warning' ? (<WarningIcon color='#FF8800' />) : messageType === 'failed' ? (<FailedIcon color='#D81010' height={'50px'} width={'50px'} />) : ''}
                    </Box>
                    {messageType && <Typography sx={{ ...CustomStyles.errorTitle, color: messageType === 'warning' ? '#FF8800' : messageType === 'failed' ? '#D81010' : '' }}>{messageType === 'warning' ? 'Warning' : messageType === 'failed' ? 'Failed' : ''}</Typography>}
                </Fragment>
            }
            <Typography
                sx={{
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    marginTop: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                }}
            >
                {message}
                {handleReload && <Button variant='text' onClick={handleReload} sx={{ textTransform: 'capitalize' }}> <ReloadIcon /> Refresh</Button>}
            </Typography>
        </Box>
    );
};

const UseCasePopup: React.FC<any> = ({ handleDataReload, isGrafanaTemplateLoading, isGrafanaTemplateError, isOntologyDataLoading, ontologyDataHasError, assetsLoading, assetsRequestFailed, assetsPropertiesLoading, assetsPropertiesRequestFailed, isOpen, onClose, onSubmit, isEdit, selectedTab, data, ontologyData, templateData, assets, assetPropertiesData, useCaseData }) => {
    const themeData: any = useTheme();
    const isViewMode = data?.mode === 'view'; // Determine if the popup is in view mode
    const dispatch = useDispatch();
    const [formData, setFormData] = useState<any>({});
    const [selectedType, setSelectedType] = useState<string>(''); // Tracks the selected radio button
    const [use_case_category, setCategory] = useState<string>(''); // Tracks the selected radio button
    const [activeStep, setActiveStep] = useState<number>(0);
    const [steps, setSteps] = useState<any[]>([]); // Tracks the steps for the stepper
    const [isStepperStarted, setIsStepperStarted] = useState<boolean>(false); // Tracks if the stepper has started
    const [validationErrors, setValidationErrors] = useState<any>({}); // Tracks validation errors
    const [filteredAssets, setFilteredAssets] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>(''); // State for search term
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const [mappingTab, setMappingTab] = useState<any>(0); // State for mapping tab
    const [mappingFields, setMappingFields] = useState<any>({ variableMapping: [], valueMapping: [] }); // State for mapping fields

    const isOntoAssetsLoading = isOntologyDataLoading || assetsLoading;
    const isPropertiesLoading = assetsPropertiesLoading;

    const ontoAssetsHasError = ontologyDataHasError || assetsRequestFailed;
    
    const assetPropertyHasError = assetsPropertiesRequestFailed || false;
    const isDataValid =
        Array.isArray(ontologyData) && ontologyData.length > 0 &&
        Array.isArray(assets) && assets.length > 0;
    const isPropertyDataValid = Array.isArray(assetPropertiesData) && assetPropertiesData.length > 0;
    const isTemplateDataValid = Array.isArray(templateData) && templateData.length > 0;

    const isNextDisabledForAssets = selectedType === 'grafanaDashboard' ? isOntoAssetsLoading || ontoAssetsHasError || !isDataValid : false;
    const isNextDisabledForAssetProperty = selectedType === 'grafanaDashboard' ? isPropertiesLoading || assetPropertyHasError || !isPropertyDataValid : false;
    const isNextDisabledForTemplate = selectedType === 'grafanaDashboard' ? isGrafanaTemplateLoading || isGrafanaTemplateError || !isTemplateDataValid : false;

    const jsonTemplate: any = [
        {
            template_id: 'custom',
            template_name: 'Custom Template',
            template_config: '',
            selected_template: 'custom', // Default selected option
        },
        ...templateData, // Spread the existing options
    ];

    useEffect(() => {
        if (data) {
            if (selectedTab === 1) {
                setFormData((prevFormData: any) => ({
                    ...prevFormData,
                    ...data,
                    template_id: data.template_id !== null ? data.template_id : 0, // Set template_id to 0 if it's null
                    selected_template: data.selected_template !== null ? String(data.selected_template) : 'custom',
                }));
            } else {
                setFormData((prevFormData: any) => ({
                    ...prevFormData,
                    ...data,
                    mapping: data.variable_mappings,
                    template_id: data.template_id !== null ? data.template_id : 'custom', // Set template_id to 0 if it's null
                    selected_template: data.selected_template !== null ? data.selected_template : 0, // Select "Custom Template" if template_id is null
                    assets_ids: data.assets_ids || [], // Convert to single-dimensional array
                }));

                setSelectedItems(data.assets_ids || []);
                setCategory(data.use_case_category || ''); // Set the selected category
                setSelectedType(data.use_case_type || ''); // Set the selected type
                setIsStepperStarted(true); // Start the stepper
            }

        } else {
            setFormData({
                ...formData,
                template_id: '', // Default to "Custom Template"
                selected_template: ''
            });
        }
    }, [data]);

    useEffect(() => {
        if (assets && isOpen) {
            const uniqueAssets = getUniqueAssets(assets);
            setFilteredAssets(uniqueAssets);
            // dispatch(connectivityDashboardActions.getAssetPropertiesDataRequest()); // Fetch asset properties data
        }
    }, [assets, isOpen])

    useEffect(() => {
        if (isStepperStarted) {
            if (selectedType === 'customApplication') {
                setSteps(stepperConfig.customApplication);
            } else if (selectedType === 'grafanaDashboard') {
                setSteps(stepperConfig.grafanaDashboard);
            } else {
                setSteps([]); // Reset steps if selectedType is invalid
            }
        }
    }, [selectedType, isStepperStarted]);

    useEffect(() => {
        if (!isOpen) {
            // Reset states when the form is closed
            setActiveStep(0);
            setIsStepperStarted(false);
            setCategory(''); // Set the selected category
            setSelectedType('');
            setFormData({});
            setValidationErrors({});
        }
    }, [isOpen]);

    useEffect(() => {
        if (selectedType === 'grafanaDashboard' && activeStep === 4) {
            const _formdatatemplate: any = typeof formData.template_config === 'string' ? JSON.parse(formData.template_config) : formData.template_config;
            const templateList = _formdatatemplate?.templating?.list || [];
            const variableMapping = templateList.filter((item: any) => item.name && item.name.startsWith('fixedmap_')) || [];
            const valueMapping = templateList.filter((item: any) => item.name && item.name.startsWith('fixedval_')) || [];
            setMappingFields({
                variableMapping: variableMapping || [], valueMapping: valueMapping || []
            })
        }
    }, [selectedType, steps, activeStep, formData])

    const assetsMappingData = useMemo(() => {
        if (selectedType === 'grafanaDashboard' && activeStep === 4) {
            const selectedAssets: any = formData.assets_ids.map((asset: any) => asset.asset_id) || [];
            const filteredTransformedAssets = Array.isArray(assetPropertiesData)
                ? assetPropertiesData
                    .filter((asset: any) => Array.isArray(selectedAssets) && selectedAssets.includes(asset.asset_id))
                    .flatMap((asset: any) => {
                        if (asset.properties && typeof asset.properties === 'object') {
                            return Object.entries(asset.properties).map(([key, value]) => ({
                                key: `${asset.asset_id}_${key}`,
                                label: key,
                                value: key,
                            }));
                        }
                        return [];
                    })
                : [];
            return filteredTransformedAssets;
        } else {
            return [];
        }
    }, [assetPropertiesData, formData])

    const handleStart = () => {
        if (selectedType) {
            setIsStepperStarted(true); // Start the stepper
            setActiveStep(0); // Reset the active step
            setFormData({}); // Reset the form data
        }
    };

    const handleNext = (event: any) => {
        event.preventDefault();
        const currentStepFields = steps[activeStep]?.fields || [];
        const errors: any = {};
        // Validate fields in the current step
        currentStepFields.forEach((field: any) => {
            const value = formData[field.name];
            if (field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
                if (field.name === 'template_id') {
                    if (value === null || (typeof value !== 'number' && value !== 0)) {
                        errors[field.name] = 'Please select a valid Grafana Template.';
                    }
                } else {
                    errors[field.name] = `Please fill out the ${field.label} field.`;
                }

            } else if (field.name === 'application_name' && !/^(?=.*[a-zA-Z])[a-zA-Z0-9@_]+$/.test(value)) {
                errors[field.name] = 'Application Name must contain letter and can include numbers, @, and _. Spaces are not allowed.';
            } else if (field.name === 'application_url') {
                const urlRegex = /^(http?:\/\/)([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*)(:\d+)?(\/.*)?$/;
                if (value && !urlRegex.test(value)) {
                    errors[field.name] = 'Please enter a valid URL for the Application (e.g., http://localhost:3001).';
                }
            } else if (field.name === 'component_name' && !/^(?=.*[a-zA-Z])[a-zA-Z0-9@_]+$/.test(value)) {
                errors[field.name] = 'Component Name must contain letter and can include numbers, @, and _. Spaces are not allowed.';
            } else if (field.name === 'navigation_name') {
                if (!/^(?=.*[a-zA-Z])[a-zA-Z0-9 ]+$/.test(value)) {
                    errors[field.name] = 'Navigation Name must contain letter and can include numbers and spaces.';
                } else if (value.length < minLength) {
                    errors[field.name] = 'Navigation Name must be at least 3 characters long.';
                } else if (value.length > maxLength) {
                    errors[field.name] = 'Navigation Name must not exceed 50 characters.';
                }
            } else if (field.name === 'use_case_name') {
                if (!/^(?=.*[a-zA-Z])[a-zA-Z0-9 ]+$/.test(value)) {
                    errors[field.name] = 'Use Case Name must contain letter and can include numbers and spaces.';
                } else if (value.length < minLength) {
                    errors[field.name] = 'Use Case Name must be at least 3 characters long.';
                } else if (value.length > maxLength) {
                    errors[field.name] = 'Use Case Name must not exceed 50 characters.';
                }
                const isDuplicate = useCaseData.some(
                    (useCase: any) =>
                        useCase.use_case_name.toLowerCase() === value.toLowerCase() &&
                        useCase.use_case_type === selectedType && // Ensure the type matches the current type
                        (!data || useCase.use_case_id !== data.use_case_id) // Exclude the current use case in edit mode
                );
                if (isDuplicate) {
                    errors[field.name] = 'Use Case Name already exists. Please choose a different name.'
                }
            } else if (field.name === 'navigation_slug' && !/^(?![_-]+$)(?![0-9_-]*[_-][0-9_-]*$)[a-zA-Z0-9_-]+$/.test(value)) {
                errors[field.name] = 'Navigation Slug must contain only letters, numbers, or a combination of letters with underscores (_) or hyphens (-). Spaces are not allowed.';
            } else if (field.name === 'template_config') {
                try {
                    if (typeof value === 'string' && value.trim() !== '') {
                        const parsedValue = JSON.parse(value); // Validate JSON format
                        if (typeof parsedValue === 'object' && Object.keys(parsedValue).length === 0) {
                            errors[field.name] = 'Template Config cannot be empty. Please provide a valid JSON object.';
                        }
                    } else if (typeof value === 'object' && Object.keys(value).length === 0) {
                        errors[field.name] = 'Template Config cannot be empty. Please provide a valid JSON object.';
                    } else if (typeof value !== 'object' || value === null) {
                        errors[field.name] = 'Please provide a valid JSON object for Template Config.';
                    }
                } catch (error) {
                    errors[field.name] = 'Please provide a valid JSON object for Template Config.';
                }
            }
        });

        if (steps[activeStep]?.label === 'Assets Config' && (!formData.assets_ids || formData.assets_ids.length === 0)) {
            errors.assets_ids = 'Please select asset(s).';
        }

        if (steps[activeStep]?.label === 'Data Source') {
            const _form: any = typeof formData.template_config === 'string' ? JSON.parse(formData.template_config) : formData.template_config;
            const templateList = _form?.templating?.list || [];
            templateList.forEach((item: any) => {
                if (item.name && item.name.startsWith('fixedmap_')) {
                    const options = assetsMappingData;
                    const selectedValue = item.query || '';
                    const isValidValue = Array.isArray(options) && options.some(opt => opt.value === selectedValue);

                    if (!selectedValue) {
                        errors[`templating_${item.name}`] = `Please select a mapping for "${item.name.replace(/^fixedmap_/, '')}".`;
                    } else if (!isValidValue) {
                        errors[`templating_${item.name}`] = `Please select a mapping for "${item.name.replace(/^fixedmap_/, '')}".`;
                    }
                }

                if (item.name && item.name.startsWith('fixedval_') && (!item.query || item.query.trim() === '')) {
                    errors[`templating_${item.name}`] = `Please enter a value for "${item.name.replace(/^fixedval_/, '')}".`;
                }
            });
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors); // Set validation errors
            return; // Stop if there are validation errors
        }

        setFormData((prevFormData: any) => ({
            ...prevFormData,
            onto_manage_id: prevFormData.onto_manage_id || '',
            assets_ids: prevFormData.assets_ids || [],
        }));

        // Clear validation errors and move to the next step
        setValidationErrors({});
        if (activeStep < steps.length - 1) {
            setActiveStep((prev) => prev + 1);
        } else {
            handleSubmit(event); // Submit on the last step
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prev) => prev - 1);
        } else {
            setIsStepperStarted(false); // Go back to the type selection screen
        }

        setFormData((prevFormData: any) => ({
            ...prevFormData,
            onto_manage_id: prevFormData.onto_manage_id || '',
            assets_ids: prevFormData.assets_ids || [],
        }));
    };

    const handleSearch = (searchTerm: string) => {
        setSearchTerm(searchTerm);
    };

    const getUniqueAssets = (assets: any[], searchTerm: string = '') => {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const filtered = assets.filter((asset: any) =>
            asset.asset_name.toLowerCase().includes(lowerCaseSearchTerm)
        );

        // Remove duplicate assets based on asset_id
        return Array.from(
            new Map(filtered.map((asset: any) => [asset.asset_id, asset])).values()
        );
    };

    const handleFieldChange = (name: string, value: any) => {
        if (!isViewMode) {
            setFormData((prev: any) => {
                const updatedFormData = { ...prev, [name]: value };

                if (name === 'selected_template') {
                    const selectedTemplate = jsonTemplate.find(
                        (template: any) => template.template_id == value?.target?.value
                    ) || { template_id: 'custom', template_name: 'Custom Template', template_config: '' };

                    if (selectedTemplate.template_id != null || selectedTemplate.template_id != 0 || selectedTemplate.template_id != '') {
                        updatedFormData.selected_template = String(selectedTemplate.template_id);
                        updatedFormData.template_config =
                            typeof selectedTemplate.template_config === 'string'
                                ? selectedTemplate.template_config !== '' ? JSON.parse(selectedTemplate.template_config) : ''
                                : selectedTemplate.template_config;
                    }
                    setValidationErrors((prevErrors: any) => {
                        const updatedErrors = { ...prevErrors };
                        delete updatedErrors.template_config;
                        return updatedErrors;
                    });
                }

                if (name === 'template_id') {
                    const selectedTemplate = jsonTemplate.find(
                        (template: any) => template.template_id == value?.target?.value
                    ) || { template_id: 'custom', template_name: 'Custom Template', template_config: '' };

                    updatedFormData.template_id = String(selectedTemplate.template_id);
                    if (selectedTemplate.template_id != null || selectedTemplate.template_id != 0 || selectedTemplate.template_id != '') {

                        updatedFormData.template_config =
                            typeof selectedTemplate.template_config === 'string'
                                ? selectedTemplate.template_config !== '' ? JSON.parse(selectedTemplate.template_config) : ''
                                : selectedTemplate.template_config;
                    }

                    setValidationErrors((prevErrors: any) => {
                        const updatedErrors = { ...prevErrors };
                        delete updatedErrors.template_config;
                        return updatedErrors;
                    });
                }

                if (name.startsWith('variable_')) {
                    const variableName = name.replace('variable_', ''); // Extract the variable name
                    updatedFormData.mapping = {
                        ...updatedFormData.mapping,
                        [variableName]: value?.target?.value || '', // Save the mapping as { variable: value }
                    };
                }

                return updatedFormData;
            });

            if (name === 'assets_ids') {
                setSelectedItems(value); // Update selected items
            }

            // Clear the validation error for the specific field
            setValidationErrors((prevErrors: any) => {
                const updatedErrors = { ...prevErrors };
                delete updatedErrors[name]; // Remove the error for the resolved field
                return updatedErrors;
            });
        }
    };

    const handleFileUpload = (name: string, files: FileList | null) => {
        if (files && files.length > 0) {
            const file = files[0];
            if (file.type === 'image/svg+xml') {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const svgContent = event.target?.result as string; // Extract SVG content as a string
                    setFormData((prev: any) => ({
                        ...prev,
                        [name]: {
                            file, // Store the file object
                            svgContent, // Store the SVG content as a string
                            filename: file.name
                        },
                    }));
                };
                reader.readAsText(file); // Read the file content as text
            } else {
                setValidationErrors((prev: any) => ({
                    ...prev,
                    [name]: 'Only SVG files are allowed.',
                }));
            }
        } else {
            // If no file is uploaded, keep the existing SVG content
            setFormData((prev: any) => ({
                ...prev,
                [name]: prev[name], // Retain the existing SVG content
            }));
        }
    };

    const handleFileRemove = (name: string) => {
        setFormData((prev: any) => ({
            ...prev,
            [name]: null, // Clear the file from formData
        }));
    };

    const handleAssetsSelected = useCallback((selectedAssets: any) => {
        setFormData((prevFormData: any) => ({
            ...prevFormData,
            assets_ids: selectedAssets, // Update assets_ids in formData
        }));
        setValidationErrors((prevErrors: any) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors.assets_ids; // Clear validation errors for assets_ids
            return updatedErrors;
        });
    }, []);

    const handleSubmit = (event: any) => {
        event.preventDefault();
        let isValid = false;
        let validate;
        const errors: any = {};
        // Create a copy of formData to safely modify template_config
        const updatedFormData = { ...formData };

        // Parse template_config if it's a string
        if (typeof updatedFormData.template_config === 'string') {
            try {
                updatedFormData.template_config =
                    updatedFormData.template_config !== ''
                        ? JSON.parse(updatedFormData.template_config)
                        : {};
            } catch (error) {
                // console.error('Invalid JSON for template_config:', error);
                updatedFormData.template_config = {}; // Fallback to an empty object
            }
        }

        if (selectedTab === 0) {
            // Validate based on selectedType
            if (selectedType === 'customApplication') {
                validate = validateCustomApplication;
            } else if (selectedType === 'grafanaDashboard') {
                // Add validation for assets_ids
                if (!updatedFormData.assets_ids || updatedFormData.assets_ids.length === 0) {
                    errors.assets_ids = 'Please select asset(s).';
                }
                //convert updatedFormData.template_id to string if it's not 'string'                
                if (typeof updatedFormData.template_id !== 'string') {
                    updatedFormData.template_id = String(updatedFormData.template_id);
                }

                if (Object.keys(errors).length > 0) {
                    setValidationErrors(errors); // Set validation errors
                    return; // Stop if there are validation errors
                }

                validate = validateGrafanaDashboard;

                // Modify template_config for grafanaDashboard
                if (updatedFormData.template_config) {
                    // Create a shallow copy of template_config to avoid modifying a frozen object
                    const templateConfigCopy = { ...updatedFormData.template_config };
                    if (data?.mode === 'edit') {
                        // add condition for tempalte creation
                        templateConfigCopy.id = updatedFormData.grafana_id;
                        templateConfigCopy.uid = updatedFormData.grafana_uid;
                        templateConfigCopy.title = updatedFormData.use_case_name; // Set title to use_case_name
                    } else {
                        delete templateConfigCopy.id; // Remove id
                        delete templateConfigCopy.uid; // Remove uid
                        templateConfigCopy.title = updatedFormData.use_case_name; // Set title to use_case_name
                    }
                    updatedFormData.template_config = templateConfigCopy; // Assign the modified copy back
                }
            }

            // Validate the formData
            if (validate) {
                isValid = validate(updatedFormData);
            }

            if (!isValid) {
                // Map validation errors to fields
                validate?.errors?.forEach((err) => {
                    const fieldName = err.instancePath.replace('/', '');
                    errors[fieldName] = err.message;
                });
                setValidationErrors(errors); // Set validation errors
                return; // Stop if there are validation errors
            }
        } else {
            // Validate JSON Template Form
            validate = validateJsonTemplateForm;

            // Validate the formData
            if (validate) {
                isValid = validate(updatedFormData);
            }

            if (!isValid) {
                // Map validation errors to fields
                validate?.errors?.forEach((err) => {
                    const fieldName = err.instancePath.replace('/', ''); // Extract field name from instancePath
                    errors[fieldName || err.params.missingProperty] = err.message; // Handle missing properties
                });

                setValidationErrors(errors); // Set validation errors
                return; // Stop if there are validation errors
            }
        }
        // Clear validation errors
        setValidationErrors({});

        // Prepare the payload
        let payload: any = {};
        if (selectedTab === 0) {
            if (selectedType === 'customApplication') {
                payload = {
                    use_case_category: use_case_category || '',
                    use_case_name: updatedFormData.use_case_name || '',
                    application_name: updatedFormData.application_name || '',
                    application_url: updatedFormData.application_url || '',
                    component_name: updatedFormData.component_name || '',
                    navigation_name: updatedFormData.navigation_name || '',
                    navigation_slug: updatedFormData.navigation_slug || '',
                    navigation_icon: JSON.stringify(updatedFormData.navigation_icon),
                    use_case_type: selectedType,
                };

                if (data && data.mode === 'edit') {
                    payload = {
                        ...payload,
                        use_case_id: data.use_case_id,
                        status: data.status || 'draft',
                    };
                }
            } else if (selectedType === 'grafanaDashboard') {
                payload = {
                    use_case_category: use_case_category || '',
                    use_case_name: updatedFormData.use_case_name || '',
                    navigation_name: updatedFormData.navigation_name || '',
                    navigation_slug: updatedFormData.navigation_slug || '',
                    navigation_icon: JSON.stringify(updatedFormData.navigation_icon),
                    template_id: (updatedFormData.template_id === 'custom') ? null : Number(updatedFormData.template_id) || null,
                    template_config: updatedFormData.template_config || {},
                    onto_manage_id: updatedFormData.onto_manage_id || 0,
                    assets_ids: updatedFormData.assets_ids || [],
                    variable_mappings: updatedFormData.mapping || {},
                    use_case_type: selectedType,
                };
                if (data && data.mode === 'edit') {
                    payload = {
                        ...payload,
                        use_case_id: data.use_case_id,
                        status: data.status,
                    };
                }
            }
        } else {
            payload = {
                template_name: updatedFormData.template_name || '',
                selected_template: updatedFormData.selected_template === 'custom' ? null : Number(updatedFormData.selected_template) || null,
                template_config: updatedFormData.template_config || {},
                template_type: 'custom',
                template_description: updatedFormData.template_description || '',
            };
            if (data && data.mode === 'edit') {
                payload = {
                    ...payload,
                    template_id: data.template_id,
                    grafana_id: data.grafana_id,
                    grafana_uid: data.grafana_uid,
                };
            }
        }
        // console.log(payload, 'payload')
        onSubmit(payload); // Call the submit handler with the payload
        handleCancel();
    };

    const handleCancel = () => {
        setCategory('');
        setSelectedType('');
        setActiveStep(0);
        setFormData({});
        setIsStepperStarted(false);
        setValidationErrors({});
        onClose();
        setMappingTab(0);
    };

    const renderStepContent = (step: any) => {
        if (!step || !step.fields) {
            return null; // Return null if step or fields are undefined
        }

        return step.fields.map((field: any) => {
            if (field.type === 'text') {
                return (
                    <Grid container key={field.name} >
                        <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, paddingTop: 0, height: '42px', display: 'flex', alignItems: 'center', '&.MuiGrid-item': { paddingTop: 0, justifyContent: 'flex-end' } }}>
                            <Typography sx={{ ...Styles.inputLabel, marginRight: '12px', color: themeData.palette.text.primary }}>
                                {field.label} {field.required && <span style={{ color: themeData.palette.error.main }}>*</span>}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} sx={{ height: '73px', '&.MuiGrid-item': { padding: 0 } }}>
                            <TextField
                                fullWidth
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                placeholder={`Enter ${field.label}`}
                                variant="outlined"
                                size="small"
                                required={field.required}
                                disabled={isViewMode}
                                error={!!validationErrors[field.name]} // Highlight field in red if there's an error
                                helperText={validationErrors[field.name] || ''} // Display validation error message
                                sx={{
                                    ".MuiFormHelperText-root": {
                                        marginTop: '2px',
                                        whiteSpace: 'normal', // Allow text to wrap
                                        lineHeight: '1.2', // Adjust line height for better readability
                                    },
                                    height: 'auto', // Allow the field to grow dynamically
                                }}
                                autoFocus={steps[activeStep]?.fields[0]?.name === field.name} // Autofocus the first field of each step
                            />
                        </Grid>
                    </Grid>
                );
            } else if (field.type === 'select') {
                return (
                    <Grid container key={field.name} >
                        <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, display: 'flex', alignItems: 'center', paddingTop: 0, height: '42px', '&.MuiGrid-item': { paddingTop: '0px !important', pl: '0px !important', justifyContent: 'flex-end' } }}>
                            <Typography sx={{ ...Styles.inputLabel, marginRight: '12px', color: themeData.palette.text.primary }}>
                                {field.label} {field.required && <span style={{ color: themeData.palette.error.main }}>*</span>}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} sx={{ height: '73px', '&.MuiGrid-item': { padding: 0 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', alignItems: 'center' }}>
                                <SelectComponent
                                    autoFocus={steps[activeStep]?.fields[0]?.name === field.name}
                                    name={field.name}
                                    options={jsonTemplate.map((item: any) => ({ label: item.template_name, value: String(item.template_id) }))}
                                    value={(formData[field.name] !== undefined) ? String(formData[field.name]) : ''}
                                    onChange={(value: any) => handleFieldChange(field.name, value)}
                                    placeholder="Select an Option"
                                    disabled={isViewMode || isGrafanaTemplateLoading}
                                    error={!!validationErrors[field.name]} // Highlight field in red if there's an error                                
                                />
                                {field.name === 'template_id' && (
                                    isGrafanaTemplateLoading ? (
                                        <IconButton sx={{ marginLeft: '4px' }}>
                                            <CircularProgress size={24} />
                                        </IconButton>
                                    ) : (
                                        <Tooltip title="Refresh Template List" arrow placement="right">
                                            <IconButton onClick={() => handleDataReload('template')} sx={{ marginLeft: '4px' }}>
                                                <ReloadIcon />
                                            </IconButton></Tooltip>)
                                )}
                            </Box>

                            {validationErrors[field.name] && (
                                <Typography variant="caption" color="error" sx={{ marginLeft: '14px', display: 'block', marginTop: '-4px', padding: 0 }}>
                                    {validationErrors[field.name]}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                );
            } else if (field.type === 'json') {
                return (
                    <Grid container key={field.name} >
                        <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, paddingTop: '0 !important', '&.MuiGrid-item': { paddingTop: '0 !important', pl: '0px !important', justifyContent: 'flex-end' } }}>
                            <Typography sx={{ ...Styles.inputLabel, marginRight: '12px', color: themeData.palette.text.primary }}>
                                {field.label} {field.required && <span style={{ color: themeData.palette.error.main }}>*</span>}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} sx={{ '&.MuiGrid-item': { padding: 0 } }}>
                            <JsonEditor
                                code={
                                    typeof formData[field.name] === 'string'
                                        ? formData[field.name]
                                        : JSON.stringify(formData[field.name], undefined, 2)
                                }
                                // readOnly={isViewMode || formData['template_config'] != 0}
                                onChange={(value: any) => handleFieldChange(field.name, value)}
                                height="225px"
                                readOnly={isViewMode}
                                styles={{
                                    border: `1px solid ${validationErrors[field.name] ? themeData.palette.error.main : themeData.palette.customBorderColor.main}`,
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                }}
                            />
                            {validationErrors[field.name] && (
                                <Typography variant="caption" color="error">
                                    {validationErrors[field.name]}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                );
            } else if (field.type === 'file') {
                return (
                    <Grid container key={field.name} >
                        <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, display: 'flex', alignItems: 'center', paddingTop: 0, height: '42px', '&.MuiGrid-item': { paddingTop: '0 !important', pl: '0px !important', justifyContent: 'flex-end' } }}>
                            <Typography sx={{ ...Styles.inputLabel, marginRight: '12px', color: themeData.palette.text.primary }}>
                                {field.label} {field.required && <span style={{ color: themeData.palette.error.main }}>*</span>}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} sx={{ height: '73px', '&.MuiGrid-item': { padding: 0 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Button
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        padding: '0px 0px 0px 16px',
                                        justifyContent: 'space-between',
                                        backgroundColor: isViewMode ? '#e0e0e0' : `${palette.primary.contrastText} !important`,
                                        borderColor: validationErrors[field.name]
                                            ? themeData.palette.error.main
                                            : darken(themeData.palette.background.default, 0.2),
                                        textTransform: 'lowercase',
                                        ':hover': {
                                            backgroundColor: `${palette.primary.contrastText} !important`,
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: darken(themeData.palette.background.default, 0.2)
                                        }
                                    }}
                                    variant="outlined"
                                    disabled={isViewMode}
                                    component="label"
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            fontWeight: 400,
                                            fontStyle: 'italic',
                                            fontSize: '16px',
                                            lineHeight: '100%',
                                            letterSpacing: '0%',
                                            verticalAlign: 'middle',
                                            color: isViewMode ? palette.disabledColor.contrastText : themeData.palette.text.secondary,
                                            flexDirection: 'row',
                                            textTransform: 'capitalize'
                                        }}
                                    >
                                        {formData[field.name]?.filename ? '1 File Selected' : 'Select File'}
                                    </div>
                                    <div
                                        style={{
                                            width: '105px',
                                            height: 'auto',
                                            borderRadius: '8px',
                                            gap: '8px',
                                            borderWidth: '1px',
                                            paddingTop: '10px',
                                            paddingRight: '16px',
                                            paddingBottom: '10px',
                                            paddingLeft: '16px',
                                            borderLeft: `1px solid ${validationErrors[field.name]
                                                ? themeData.palette.error.main
                                                : darken(themeData.palette.background.default, 0.2)
                                                }`,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            textTransform: 'uppercase',
                                        }}

                                    >
                                        Browse
                                    </div>
                                    <VisuallyHiddenInput
                                        type="file"
                                        disabled={isViewMode}
                                        accept=".svg" // Restrict file type to SVG
                                        onChange={(e: any) => {
                                            const file = e.target.files[0];
                                            if (file && file.type === 'image/svg+xml') {
                                                handleFileUpload(field.name, e.target.files);
                                            } else {
                                                // Show validation error if the file is not an SVG
                                                setValidationErrors((prev: any) => ({
                                                    ...prev,
                                                    [field.name]: 'Only SVG files are allowed.',
                                                }));
                                            }
                                        }}
                                        onClick={(event: any) => {
                                            event.target.value = null;
                                        }}
                                    />
                                </Button>
                            </Box>
                            <Box sx={{ marginLeft: '16px', marginTop: '4px' }}>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '14px', lineHeight: '0px' }}>
                                    Only SVG icon files are allowed. Recommended size: 16x16px.
                                </Typography>
                            </Box>
                            {validationErrors[field.name] && (
                                (!formData[field.name]?.filename) && (
                                    <Typography variant="caption" color="error" sx={{ marginLeft: '16px', marginTop: '-1px', marginBottom: '0px' }}>
                                        {validationErrors[field.name]}
                                    </Typography>)
                            )}
                            {formData[field.name]?.filename && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        marginLeft: '16px',
                                        marginTop: '10px',
                                        color: themeData.palette.text.primary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '1rem',
                                    }}
                                >
                                    {formData[field.name]?.filename}
                                    <Button
                                        disabled={isViewMode}
                                        onClick={() => handleFileRemove(field.name)}
                                        sx={{
                                            marginLeft: '8px',
                                            color: themeData.palette.error.main,
                                            minWidth: 'auto',
                                            padding: '0',
                                            marginTop: '2px',
                                            '&:disabled': {
                                                backgroundColor: 'transparent', // Remove background when disabled
                                            },
                                        }}
                                    >
                                        <CloseIcon style={{ width: '24px', height: '24px', color: '#51648F' }} />
                                    </Button>
                                </Typography>
                            )}


                        </Grid>
                    </Grid>
                );
            } else if (field.type === 'checkboxList') {
                return (
                    <Fragment key={'AssetsList_Fragmetn' + field.name} >
                        {/* <Grid item xs={12} paddingLeft={'16px'} > <Divider sx={{ marginBottom: '16px', marginTop: '8px' }} /></Grid> */}
                        <Grid container key={'AssetsList_' + field.name} sx={{ paddingLeft: '16px', paddingTop: '8px' }}>
                            {
                                isOntologyDataLoading ? (<CenteredMessageBox message="Loading Ontology Data..." showLoading={true} />
                                ) : ontologyDataHasError ? (<CenteredMessageBox handleReload={() => handleDataReload('ontology')} messageType='failed' message="Unable to retrieve ontology data. Please check your connection and try reloading the page." showLoading={false} />
                                ) : ontologyData.length === 0 ? (<CenteredMessageBox handleReload={() => handleDataReload('ontology')} messageType='warning' message="No ontology data found. Please ensure ontology is configured before proceeding." showLoading={false} />
                                ) : assetsLoading ? (<CenteredMessageBox message="Loading Assets Data..." showLoading={true} />
                                ) : assetsRequestFailed ? (
                                    <CenteredMessageBox messageType='failed' handleReload={() => handleDataReload('assets')} message="Unable to fetch assets data. Please check your connection and try reloading the page." showLoading={false} />
                                ) : assets.length === 0 ? (
                                    <CenteredMessageBox messageType='warning' handleReload={() => handleDataReload('assets')} message="No assets available. Please add assets to continue." showLoading={false} />
                                ) :
                                    <Fragment>
                                        <Grid item xs={12} >
                                            <Grid container >
                                                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', height: '48px' }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', height: '24px' }}>
                                                            <CardHeader title="List of Asset(s)" titleTypographyProps={Styles.title} sx={{ padding: 0 }} />
                                                            <Tooltip title="Refresh Assets Data" arrow placement="right">
                                                                <IconButton onClick={() => handleDataReload('assets')} sx={{ marginLeft: '4px', height: '48px' }}>
                                                                    <ReloadIcon />
                                                                </IconButton></Tooltip>
                                                        </Box>

                                                        {validationErrors.assets_ids && (
                                                            <Box ><Typography
                                                                variant="caption"
                                                                color="error"
                                                                sx={{ marginTop: '0px', fontSize: '14px', display: 'block', textAlign: 'right' }}
                                                            >
                                                                {validationErrors.assets_ids}
                                                            </Typography></Box>
                                                        )}
                                                    </Box>

                                                </Grid>
                                                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                    {!isViewMode && <SearchField searchPlaceholder='Search Ontology / Asset' onSearch={handleSearch} />}
                                                </Grid>
                                            </Grid>

                                        </Grid>
                                        <Grid item xs={12} paddingTop={'24px'}>
                                            <>
                                                <AssetsTreeView ontologyData={ontologyData} assetsData={filteredAssets} searchTerm={searchTerm} savedAssets={formData.assets_ids || []} onAssetsSelected={handleAssetsSelected} readonly={isViewMode} />

                                            </>
                                        </Grid>
                                    </Fragment>
                            }
                        </Grid >
                    </Fragment >
                );
            } else if (field.type === 'dataGrid') {
                return (
                    <Grid container key={field.name} sx={{ padding: '16px', paddingRight: '0px', paddingTop: '0px', marginTop: '-24px', width: '101%', paddingBottom: '0px' }}>
                        <Grid item xs={12} sx={{ minHeight: '300px', overflowY: 'hidden' }}>
                            {
                                assetsPropertiesLoading ? (
                                    <CenteredMessageBox message="Loading Assets Mapping..." showLoading={true} />
                                ) : assetPropertiesData.length === 0 ? (
                                    <CenteredMessageBox handleReload={() => handleDataReload('assetsmapping')} message="No asset mapping data found. Please configure asset mappings to proceed." messageType='warning' showLoading={false} />
                                ) : assetPropertyHasError ? (
                                    <CenteredMessageBox handleReload={() => handleDataReload('assetsmapping')} message="Unable to load asset mapping data. Please check your connection and try reloading the page." messageType='failed' showLoading={false} />
                                ) : assetsMappingData.length === 0 ? <CenteredMessageBox handleReload={() => handleDataReload('assetsmapping')} message="Asset mapping data not found for selected assets. Please configure asset mappings to proceed." messageType='warning' showLoading={false} /> : (
                                    (mappingFields?.variableMapping?.length > 0 && mappingFields?.valueMapping?.length > 0) ? (
                                        <>
                                            <Box sx={{
                                                display: 'flex',
                                                width: '100%',
                                                justifyContent: 'space-between',
                                                borderBottom: '1px solid #D9DFED',
                                                alignItems: 'center',
                                            }}>
                                                <Tabs
                                                    value={mappingTab}
                                                    onChange={(e: any, value: number) => { setMappingTab(value) }}
                                                    TabIndicatorProps={{
                                                        style: {
                                                            backgroundColor: themeData.palette.primary.main,
                                                        },
                                                    }}
                                                    sx={{
                                                        '& .MuiTab-root': {
                                                            fontSize: '16px',
                                                        },
                                                        '& .MuiTabs-flexContainer': {
                                                            width: '100%',
                                                        }
                                                    }}
                                                >
                                                    <Tab
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                Variable Mapping
                                                                {Object.keys(validationErrors).some(key => key.startsWith('templating_fixedmap')) && (
                                                                    <Tooltip
                                                                        title={
                                                                            <Box>
                                                                                {Object.entries(validationErrors)
                                                                                    .filter(([key]) => key.startsWith('templating_fixedmap'))
                                                                                    .map(([key, msg]: any) => (
                                                                                        <div key={key}>{msg}</div>
                                                                                    ))}
                                                                            </Box>
                                                                        }
                                                                        arrow
                                                                    >
                                                                        <WarningIcon style={{ height: '20px', width: '20px', marginLeft: '5px', color: themeData.palette.error.main }} />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        }
                                                    />

                                                    <Tab
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                Value Mapping
                                                                {Object.keys(validationErrors).some(key => key.startsWith('templating_fixedval')) && (
                                                                    <Tooltip
                                                                        title={
                                                                            <Box>
                                                                                {Object.entries(validationErrors)
                                                                                    .filter(([key]) => key.startsWith('templating_fixedval'))
                                                                                    .map(([key, msg]: any) => (
                                                                                        <div key={key}>{msg}</div>
                                                                                    ))}
                                                                            </Box>
                                                                        }
                                                                        arrow
                                                                    >
                                                                        <WarningIcon style={{ height: '20px', width: '20px', marginLeft: '5px', color: themeData.palette.error.main }} />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        }

                                                    />
                                                </Tabs>
                                                {mappingTab === 0 ? assetsPropertiesLoading ? (

                                                    <IconButton sx={{ marginLeft: '4px' }}>
                                                        <CircularProgress size={24} />
                                                    </IconButton>
                                                ) : (
                                                    <Tooltip title="Refresh Mapping Data" arrow placement="right">
                                                        <IconButton onClick={() => handleDataReload('assetsmapping')} sx={{ marginLeft: '4px' }}>
                                                            <ReloadIcon />
                                                        </IconButton></Tooltip>) : null}
                                            </Box>

                                            {mappingTab === 0 && <Box
                                                sx={{
                                                    backgroundColor: themeData.palette.background.paper,
                                                    marginTop: '0px',
                                                    padding: '16px',
                                                    paddingBottom: '0px'
                                                }}
                                            >
                                                <>
                                                    <Grid container spacing={2} sx={{ marginBottom: '12px' }}>
                                                        <Grid item xs={6}>
                                                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: themeData.palette.text.secondary }}>
                                                                Data Field Name
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: themeData.palette.text.secondary }}>
                                                                Map To
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <div style={{ overflowY: 'scroll', height: '200px', paddingRight: '16px' }}>
                                                        {mappingFields?.variableMapping?.map((item: any, index: number) => {
                                                            const options = assetsMappingData;
                                                            const selectedValue = item.query || '';
                                                            const isValidValue = options.some(opt => opt.value === selectedValue);

                                                            return (
                                                                <Grid container spacing={2} key={`mappinglist_${item.name}_${index}`} sx={{ marginBottom: '8px' }}>
                                                                    <Grid item xs={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            value={item.name.replace(/^fixedmap_/, '')}
                                                                            InputProps={{ readOnly: true }}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            disabled={true}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={6} sx={{ height: '72px' }}>
                                                                        <SelectComponent
                                                                            selectkey={`select_${item.name}_${index}`}
                                                                            name={`templating_${item.name}`}
                                                                            disabled={isViewMode}
                                                                            options={options}
                                                                            value={isValidValue ? selectedValue : ''}
                                                                            onChange={(value: any) => {
                                                                                const rawVaraibleMap: any = value?.target?.value;
                                                                                setFormData((prev: any) => {
                                                                                    const _temp: any = typeof prev.template_config === 'string' ? JSON.parse(prev.template_config) : prev.template_config;

                                                                                    const updatedList = _temp.templating.list.map((listItem: any) =>
                                                                                        listItem.name === item.name
                                                                                            ? {
                                                                                                ...listItem,
                                                                                                ...(listItem.current && {
                                                                                                    current: {
                                                                                                        ...item.current,
                                                                                                        value: rawVaraibleMap,
                                                                                                        text: rawVaraibleMap
                                                                                                    }
                                                                                                }),
                                                                                                query: rawVaraibleMap,
                                                                                            }
                                                                                            : listItem
                                                                                    );
                                                                                    return {
                                                                                        ...prev,
                                                                                        template_config: {
                                                                                            ..._temp,
                                                                                            templating: {
                                                                                                ..._temp.templating,
                                                                                                list: updatedList,
                                                                                            },
                                                                                        },
                                                                                    };
                                                                                });
                                                                                handleFieldChange(`variable_${item.name}`, value)
                                                                                setValidationErrors((prev: any) => {
                                                                                    const updated = { ...prev };
                                                                                    delete updated[`templating_${item.name}`];
                                                                                    return updated;
                                                                                });
                                                                            }}
                                                                            placeholder="Select Mapping"
                                                                            helpersx={{
                                                                                marginTop: '0px'
                                                                            }}
                                                                            error={!!validationErrors[`templating_${item.name}`]}
                                                                            helperText={validationErrors[`templating_${item.name}`] || ''}
                                                                        />
                                                                        {/* {item.current.value} */}
                                                                    </Grid>
                                                                </Grid>
                                                            )
                                                        }
                                                        )}
                                                    </div>
                                                </>
                                            </Box>}
                                            {mappingTab === 1 && <Box
                                                sx={{
                                                    backgroundColor: themeData.palette.background.paper,
                                                    marginTop: '0px',
                                                    padding: '16px',
                                                    paddingBottom: '0px'
                                                }}
                                            >
                                                <>
                                                    <Grid container spacing={2} sx={{ marginBottom: '12px' }}>
                                                        <Grid item xs={6}>
                                                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: themeData.palette.text.secondary }}>
                                                                Data Field Name
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: themeData.palette.text.secondary }}>
                                                                Value
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <div style={{ overflowY: 'scroll', height: '200px', paddingRight: '16px' }}>
                                                        {mappingFields?.valueMapping?.map((item: any, index: number) => (
                                                            <Grid container spacing={2} key={`valuemappinglist_${item.name}_${index}`} sx={{ marginBottom: '8px' }}>
                                                                <Grid item xs={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        value={item.name.replace(/^fixedval_/, '')}
                                                                        InputProps={{ readOnly: true }}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disabled={true}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={6} sx={{ height: '72px' }}>
                                                                    <TextField
                                                                        fullWidth
                                                                        name={`templating_${item.name}`}
                                                                        value={item.query || ''}
                                                                        onChange={(e) => {
                                                                            // const rawValue = e.target.value || '';
                                                                            const rawValue = e.target.value || '';
                                                                            // if (item.type === 'custom') {
                                                                            //     rawValue = rawValue.replace(/\s+/g, '');
                                                                            //     // if (
                                                                            //     //     rawValue &&
                                                                            //     //     !rawValue.endsWith(',') &&
                                                                            //     //     rawValue.length > (item.query || '').length // User is typing, not deleting
                                                                            //     // ) {
                                                                            //     //     rawValue += ',';
                                                                            //     // }
                                                                            // }

                                                                            setFormData((prev: any) => {
                                                                                const _temp = typeof prev.template_config === 'string' ? JSON.parse(prev.template_config) : prev.template_config;

                                                                                const valuMappingValue = rawValue.replace(/\s+/g, '');
                                                                                const valueList = item.type === 'custom' ? valuMappingValue.split(',').filter(Boolean) : [valuMappingValue];
                                                                                const firstValue = valueList[0] || '';

                                                                                const updatedList = _temp.templating.list.map((listItem: any) =>
                                                                                    listItem.name === item.name
                                                                                        ? {
                                                                                            ...listItem,
                                                                                            ...(listItem.current && {
                                                                                                current: {
                                                                                                    ...item.current,
                                                                                                    value: listItem.type === 'custom' ? firstValue : rawValue,
                                                                                                    text: listItem.type === 'custom' ? firstValue : rawValue
                                                                                                }
                                                                                            }),
                                                                                            query: listItem.type === 'custom' ? valuMappingValue : rawValue,
                                                                                            ...(listItem.type === 'custom' && {
                                                                                                options: valueList.map((val: any, idx: any) => ({
                                                                                                    text: val,
                                                                                                    value: val,
                                                                                                    selected: idx === 0
                                                                                                }))
                                                                                            })
                                                                                        }
                                                                                        : listItem
                                                                                );
                                                                                return {
                                                                                    ...prev,
                                                                                    template_config: {
                                                                                        ..._temp,
                                                                                        templating: {
                                                                                            ..._temp.templating,
                                                                                            list: updatedList,
                                                                                        },
                                                                                    },
                                                                                };
                                                                            });
                                                                            setValidationErrors((prev: any) => {
                                                                                const updated = { ...prev };
                                                                                delete updated[`templating_${item.name}`];
                                                                                return updated;
                                                                            });
                                                                        }}
                                                                        placeholder="Enter Value"
                                                                        variant="outlined"
                                                                        size="small"
                                                                        sx={{
                                                                            "& .MuiFormHelperText-root": { marginTop: '0px' }
                                                                        }}
                                                                        disabled={isViewMode}
                                                                        error={!!validationErrors[`templating_${item.name}`]}
                                                                        helperText={validationErrors[`templating_${item.name}`] || ''}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                    </div>
                                                </>
                                            </Box>}
                                        </>
                                    ) : (
                                        mappingFields?.variableMapping?.length === 0 ?
                                            <CenteredMessageBox messageType='failed' message="No variable mappings were found in the selected template. Please review your template configuration and ensure variable mappings are defined." showLoading={false} />
                                            : mappingFields?.valueMapping?.length === 0 ? <CenteredMessageBox messageType='failed' message="No value mappings were found in the selected template. Please review your template configuration and ensure value mappings are defined." showLoading={false} /> : ''
                                    ))
                            }
                        </Grid>
                    </Grid>
                );
            }
            return null;
        });
    };

    const renderButtons = () => {
        if (selectedTab === 0) {
            // For Use Case Builder (Stepper)
            if (!isStepperStarted) {
                return (
                    <>
                        <Button variant="outlined" color="primary" onClick={handleCancel} sx={{ marginRight: '8px', width: '100px' }}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="primary" sx={{ width: '100px' }} onClick={handleStart} disabled={!use_case_category || !selectedType}>
                            Start
                        </Button>
                    </>
                );
            }

            return (
                <>
                    {/* {(!isNextDisabledForAssetProperty && !isNextDisabledForAssets) ? <> */}
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={
                            (activeStep === 0 && !isStepperStarted) ||
                            (activeStep === 0 && (isViewMode || (data && data.mode === 'edit')))
                        }
                        sx={{ marginRight: '8px', width: '112px' }}
                    >
                        {activeStep === 0 ? 'Back' : 'Previous'}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        sx={{ width: '112px' }}
                        disabled={
                            activeStep === steps.length - 1
                                ? isViewMode ? true : activeStep === 3 ? isNextDisabledForAssets
                                    : activeStep === 4 ? isNextDisabledForAssetProperty || mappingFields.variableMapping.length === 0 || mappingFields.valueMapping.length === 0
                                        : false
                                : activeStep === 3 ? isNextDisabledForAssets
                                    : activeStep === 4 ? isNextDisabledForAssetProperty || mappingFields.variableMapping.length === 0 || mappingFields.valueMapping.length === 0
                                        : false
                        }
                    >
                        {activeStep === steps.length - 1 ? 'Save' : 'Next'}
                    </Button >
                </>
            );
        } else {
            // For JSON Template Form
            return (
                <>
                    <Button variant="outlined" color="primary" onClick={handleCancel} sx={{ marginRight: '8px' }}>
                        Cancel
                    </Button>
                    {!isViewMode && <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isViewMode}>
                        Save
                    </Button>}
                </>
            );
        }
    };

    return (
        <FormModal
            title={
                selectedTab === 0
                    ? isViewMode
                        ? 'VIEW USE CASE'
                        : data?.mode === 'edit'
                            ? 'EDIT USE CASE'
                            : 'ADD NEW USE CASE'
                    : isViewMode
                        ? 'VIEW GRAFANA TEMPLATE'
                        : data?.mode === 'edit'
                            ? 'EDIT GRAFANA TEMPLATE'
                            : 'CREATE GRAFANA TEMPLATE'
            }
            isOpen={isOpen}
            handleSubmit={selectedTab === 0 ? handleNext : handleSubmit} // Use handleNext for stepper, handleSubmit for JSON Template Form
            handleClose={handleCancel}
            customButtons={renderButtons()} // Pass custom buttons dynamically
            onKeyDown={(event: any) => {
                if (event.key === 'Enter') {
                    event.preventDefault(); // Prevent form submission on Enter key press
                }
            }}
            disableOverflow={true}
        >
            {selectedTab === 0 ? (
                <>
                    {!isStepperStarted ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Grid container spacing={2} sx={{ paddingTop: '24px' }}>
                                    {/* Category Selection */}
                                    <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, paddingTop: 0, height: '42px', display: 'flex', alignItems: 'center', '&.MuiGrid-item': { paddingTop: 0, justifyContent: 'flex-end' } }}>
                                        <Typography sx={{ ...Styles.inputLabel, marginBottom: '8px' }}>
                                            Select Category <span style={{ color: themeData.palette.error.main }}>*</span>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={8} sx={{ height: '73px', '&.MuiGrid-item': { padding: 0 } }}>
                                        <SelectComponent
                                            name="category"
                                            options={[
                                                { label: 'Dashboard', value: 'dashboard' },
                                                { label: 'Application', value: 'application' },
                                            ]}
                                            value={use_case_category === undefined || use_case_category === null || use_case_category == '0' ? '' : use_case_category}
                                            onChange={(e: any) => setCategory(e.target.value)}
                                            placeholder="Select Category"
                                        />
                                    </Grid>

                                    {/* Type Selection */}

                                    <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, paddingTop: 0, height: '42px', display: 'flex', alignItems: 'center', '&.MuiGrid-item': { paddingTop: 0, justifyContent: 'flex-end' } }}>
                                        <Typography sx={{ ...Styles.inputLabel, marginBottom: '8px' }}>
                                            Select Type <span style={{ color: themeData.palette.error.main }}>*</span>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={8} sx={{ height: '73px', '&.MuiGrid-item': { padding: 0 } }}>
                                        <SelectComponent
                                            name="type"
                                            disabled={use_case_category === ''}
                                            options={
                                                use_case_category === '' ? [] : use_case_category === 'application'
                                                    ? [{ label: 'Custom Application', value: 'customApplication' }]
                                                    : [
                                                        { label: 'Grafana Dashboard', value: 'grafanaDashboard' },
                                                        { label: 'Custom Application', value: 'customApplication' },
                                                    ]
                                            }
                                            value={selectedType === undefined || selectedType === null || selectedType === '0' ? '' : selectedType}
                                            onChange={(e: any) => setSelectedType(e.target.value)}
                                            placeholder="Select Type"
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    ) : (
                        <>
                            <Stepper
                                activeStep={activeStep}
                                alternativeLabel
                                connector={
                                    <StepConnector
                                        sx={{
                                            '& .MuiStepConnector-line': {
                                                borderColor: themeData.palette.customBorderColor.main, // Default line color
                                            },
                                            '&.Mui-active .MuiStepConnector-line': {
                                                borderColor: themeData.palette.primary.main, // Active line color
                                            },
                                            '&.Mui-completed .MuiStepConnector-line': {
                                                borderColor: palette.success.dark, // Completed line color
                                            },
                                        }}
                                    />
                                }
                                sx={{
                                    marginBottom: '24px', // Add spacing below the stepper                                  
                                    '& .MuiStepLabel-label': {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: themeData.palette.text.secondary,
                                        marginTop: '8px',
                                    },
                                    '& .MuiStepIcon-root': {
                                        color: themeData.palette.customBorderColor.main, // Default circle color
                                        width: '32px',
                                        height: '32px',
                                        fontSize: '1.5rem',
                                    },
                                    '& .MuiStepIcon-root.Mui-active': {
                                        color: themeData.palette.primary.main, // Active circle color
                                    },
                                    '& .MuiStepIcon-root.Mui-completed': {
                                        color: palette.success.dark, // Completed circle color
                                    },
                                    '& .MuiStepIcon-text': {
                                        display: 'none', // Hide step numbers
                                    },
                                }}
                            >
                                {steps.map((step, index) => (
                                    <Step key={index}>
                                        <StepLabel>{step.label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <Divider />
                            <Grid container spacing={2} sx={{ marginTop: '24px', display: 'flex', flexDirection: 'column', minHeight: selectedType === 'customApplication' ? '220px' : '300px' }}>
                                {steps[activeStep] ? renderStepContent(steps[activeStep]) : null}
                            </Grid>
                            {/* </>
                            )} */}
                        </>
                    )}
                </>
            ) : (
                <>
                    {/* JSON Template Form for selectedTab !== 0 */}
                    <Grid container>
                        <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, alignItems: 'center', height: '42px', '&.MuiGrid-item': { paddingTop: '0px !important', pl: '0px !important', justifyContent: 'flex-end' } }}>
                            <Typography sx={{ ...Styles.inputLabel, marginRight: '12px', color: themeData.palette.text.primary }}>Template Name {<span style={{ color: themeData.palette.error.main }}>*</span>}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} sx={{ minHeight: '73px', '&.MuiGrid-item': { padding: '0px 0px' } }}>
                            <TextField
                                fullWidth
                                name="template_name"
                                value={formData.template_name || ''}
                                onChange={(e) => handleFieldChange('template_name', e.target.value)}
                                placeholder="Enter Template Name"
                                variant="outlined"
                                size="small"
                                error={!!validationErrors.template_name} // Highlight field in red if there's an error
                                helperText={validationErrors.template_name || ''} // Display validation error message
                                focused={!isViewMode} // Set focus on the field when the modal opens
                                autoFocus={!isViewMode} // Auto-focus only if not in view mode
                                InputProps={{
                                    readOnly: isViewMode, // Make the field read-only in view mode
                                }}
                                disabled={isViewMode} // Disable the field in view mode
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, alignItems: 'baseline', '&.MuiGrid-item': { paddingTop: '0px !important', pl: '0px !important', justifyContent: 'flex-end' } }}>
                            <Typography sx={{ ...Styles.inputLabel, marginRight: '12px', color: themeData.palette.text.primary }}>Description {<span style={{ color: themeData.palette.error.main }}>*</span>}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} sx={{ minHeight: '140px', '&.MuiGrid-item': { padding: '0px 0px' } }}>
                            <TextField
                                fullWidth
                                id='outlined-multiline-static'
                                name="template_description"
                                value={formData.template_description || ''}
                                onChange={(e) => handleFieldChange('template_description', e.target.value)}
                                placeholder="Enter Description"
                                variant="outlined"
                                size="small"
                                multiline
                                minRows={3}
                                InputProps={{
                                    readOnly: isViewMode,
                                }}
                                disabled={isViewMode}
                                sx={{
                                    '&.MuiFormControl-root': { width: '100%' },
                                    '.MuiOutlinedInput-root': { height: 'auto !important', width: '100%', minWidth: '300px' },
                                    paddingBottom: '16px',
                                }}
                                rows={4}
                                error={!!validationErrors.template_description}
                                helperText={validationErrors.template_description || ''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, alignItems: 'center', height: '42px', '&.MuiGrid-item': { paddingTop: '0px !important', pl: '0px !important', justifyContent: 'flex-end' } }}>
                            <Typography sx={{ ...Styles.inputLabel, marginRight: '12px', color: themeData.palette.text.primary }}>Schema Copy From {<span style={{ color: themeData.palette.error.main }}>*</span>}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} sx={{ minHeight: '73px', '&.MuiGrid-item': { padding: '0px 0px' } }}>
                            <SelectComponent
                                name={'selected_template'}
                                InputProps={{
                                    readOnly: isViewMode, // Make the field read-only in view mode
                                }}
                                disabled={isViewMode}
                                options={jsonTemplate.map((item: any) => ({ label: item.template_name, value: String(item.template_id) }))}
                                value={(formData.selected_template !== undefined) ? String(formData.selected_template) : ''}
                                onChange={(value: any) => handleFieldChange('selected_template', value)}
                                placeholder="Select an Option"
                                error={!!validationErrors.selected_template} // Highlight field in red if there's an error                                
                            />
                            {validationErrors.selected_template && (
                                <Typography variant="caption" color="error" sx={{ marginLeft: '16px' }}>
                                    {validationErrors.selected_template}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12} sm={12} md={4} sx={{ ...Styles.gridItem, alignItems: 'baseline', '&.MuiGrid-item': { paddingTop: '0px !important', pl: '0px !important', justifyContent: 'flex-end' } }}>
                            <Typography sx={{ ...Styles.inputLabel, marginRight: '12px', color: themeData.palette.text.primary }}>Schema {<span style={{ color: themeData.palette.error.main }}>*</span>}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8} sx={{ '&.MuiGrid-item': { padding: '0px 0px' } }}>
                            <JsonEditor
                                code={
                                    typeof formData.template_config === 'string'
                                        ? formData.template_config
                                        : JSON.stringify(formData.template_config, undefined, 2)
                                }
                                onChange={(value: any) => handleFieldChange('template_config', value)}
                                height="250px"
                                readOnly={isViewMode} // Make the field read-only in view mode
                                styles={{
                                    border: `1px solid ${validationErrors.template_config ? themeData.palette.error.main : themeData.palette.customBorderColor.main}`,
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                }}
                            />
                            {validationErrors.template_config && (
                                <Typography variant="caption" color="error" sx={{ marginLeft: '16px' }}>
                                    {validationErrors.template_config}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </>
            )
            }
        </FormModal >
    );
};

const mapStateToProps = (state: any) => ({
    // assetPropertiesData: state.connectivityDashboard.assetPropertiesData,
    // assetsPropertiesLoading: state.connectivityDashboard.assetsPropertiesLoading,
});

export default connect(mapStateToProps)(UseCasePopup);